import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { message, Dropdown, Menu } from 'antd';
import { yupResolver } from '@hookform/resolvers/yup';
import _ from 'lodash';

import { EPriceType } from 'components/NewProductStepper/ProductBaseForm/constants';
import { Product, ProductSubcategory } from 'api/types/product';
import { productUpdate } from 'api/product_sdk';
import { useProductsList } from 'api/products';

import { Button, Modal, Typography, Tabs } from 'ui-kit';
import ProductBaseForm from 'components/NewProductStepper/ProductBaseForm';
import ProductSettingsBaseForm from 'components/NewProductStepper/ProductSettingsBaseForm';
import {
  getInititalValuesFromProduct,
  validateProductEditForm,
} from 'components/NewProductStepper/utils';
import ConfirmModalMassAddProduct from 'components/ConfirmModalMassAddProduct';
import ConfirmModalMassUpdateProduct from 'components/ConfirmModalMassUpdateProduct';
import ProductPayoutSettings from 'components/NewProductStepper/ProductPayoutSettings/ProductPayoutSettings';

import './EditProductModal.scss';
import { WarningOutlined } from '@ant-design/icons';
import { useLoadingBackdrop } from 'hooks/useLoadingBackdrop';
import { validateProductPrices } from 'components/NewProductStepper/ProductBaseForm/utils';
import { getProductCategory } from 'components/NewProductStepper/ProductSettingsBaseForm/utils';
import { DEFAULT_PRODUCT_AIRBNB_INSTRUCTIONS } from 'components/NewProductStepper/SelectProduct/utils';
import { useParams } from 'react-router';
import { IS_STAGING } from 'consts';

interface Props {
  product: Product;
  isOpen: boolean;
  onCancel: () => void;
  onSuccess: (values: Partial<Product>) => void;
}

const TABS = {
  'product-description': {
    key: 'product-description',
    label: 'Product description',
    title: 'What would you like to tell guests about this product?',
    children: <ProductBaseForm mode="edit" />,
    fields: [
      'name',
      'price',
      'subcategory',
      'description',
      'cancellationPolicy',
      'instructions',
      'image',
      'priceType',
      'productPrices',
      'isPurchasableOffPlatform',
      'instructionsOffPlatform',
    ],
  },
  'product-settings': {
    key: 'product-settings',
    label: 'Product settings',
    title: 'How do you want to manage this product?',
    children: <ProductSettingsBaseForm />,
    fields: [
      'category',
      'preorderBy',
      'requiresApproval',
      'quantity',
      'alertThreshold',
      'productDelegates',
      'extraFields',
      'externalLink',
    ],
  },
};

enum EDropdownButtonOptions {
  UPDATE = 'Save changes',
  MASS_UPDATE = 'Save an update in stores that already have this tile',
  UPDATE_AND_MASS_CREATE = 'Save changes and add tile to all stores',
}

const EditProductModal: React.FC<Props> = ({ product, isOpen, onCancel, onSuccess }) => {
  const params = useParams<{ storeId: string }>();
  const storeId = useMemo(() => (params.storeId ? Number(params.storeId) : null), [params.storeId]);

  const [showLoading, hideLoading] = useLoadingBackdrop();

  const [activeTab, setActiveTab] = useState('product-description');
  const [tabWithErrors, setTabWithErrors] = useState<string | null>(null);

  const tabsConfig = useMemo(() => {
    const tabs: Record<string, any> = { ...TABS };
    if (IS_STAGING) {
      tabs['payout-settings'] = {
        key: 'payout-settings',
        label: 'Payout settings',
        title: 'How should revenue from this product be allocated?',
        children: <ProductPayoutSettings store={(product as any)?.store} />,
        fields: ['isDefaultPayoutSplit', 'isCustomPayoutSplit', 'amountSplits', 'percentageSplits'],
      };
    }
    return tabs;
  }, [product]);

  const {
    formState: { dirtyFields, isSubmitting, errors, ...restFormState },
    getValues,
    ...methods
  } = useForm({
    mode: 'onChange',
    shouldFocusError: false,
    resolver: yupResolver(validateProductEditForm),
  });

  const [buttonText, setButtonText] = useState(EDropdownButtonOptions.UPDATE);
  const [massCreate, setIsMassCreate] = useState<boolean>(false);
  const [massUpdate, setIsMassUpdate] = useState<boolean>(false);
  const [isFromGroup, setIsFromGroup] = useState<boolean>(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  useEffect(() => {
    if (isOpen && product?.id) {
      const intialValues = getInititalValuesFromProduct(product);
      methods.reset(intialValues);
    }
  }, [isOpen, product?.id]);

  useEffect(() => {
    setIsFromGroup(!!product?.productGroup);
  }, [product?.productGroup]);

  const handleChangeTab = (tab) => {
    setActiveTab(tab);
  };

  useEffect(() => {
    setIsMassCreate(buttonText === EDropdownButtonOptions.UPDATE_AND_MASS_CREATE);
    setIsMassUpdate(buttonText === EDropdownButtonOptions.MASS_UPDATE);
  }, [buttonText]);

  const { mutate: mutateProductList } = useProductsList({
    storeId,
    sort: 'store_arrangement',
  });

  const validateForm = useCallback(
    (product) => {
      let hasErrors = false;

      const currentSplits = product?.percentageSplits || [];
      const hasSplits =
        currentSplits.length > 0 &&
        currentSplits.some(
          (item: any) =>
            item?.percents !== null && item?.percents !== undefined && item?.percents !== '',
        );
      const totalPercents = _.sumBy(currentSplits, (item: any) => Number(item?.percents) || 0);

      const isPercentageSplitValid = !hasSplits || totalPercents === 100;

      if (!_.isEmpty(errors) && errors?.product) {
        const errorFields = _.keys(errors.product).filter((key) => {
          if (key === 'percentageSplits' && isPercentageSplitValid) {
            return false;
          }
          return !_.isEmpty(errors.product[key]);
        });

        _.mapKeys(tabsConfig, ({ fields }, tabName) => {
          const tabErrors = fields.filter((field) => errorFields.includes(field));

          if (tabErrors.length) {
            hasErrors = true;
            setTabWithErrors(tabName);
            const tabLabel = _.find(tabsConfig, { key: tabName })?.label;
            message.error(`There is an error in the ${tabLabel} tab`, 5);
          }
        });
      }

      const isMultiplePriceType =
        product?.priceType === EPriceType.MULTIPLE || product?.priceType === 'multiple';

      if (product && isMultiplePriceType) {
        const hasProductPriceErrors = validateProductPrices({
          productPrices: product.productPrices || [],
          priceType: product.priceType,
          setErrorCallback: methods.setError,
          isEarlyCheckin: product.isEarlyCheckin,
          isLateCheckout: product.isLateCheckout,
        });

        if (hasProductPriceErrors) {
          hasErrors = true;
          const tabLabel = _.find(tabsConfig, { key: 'product-description' })?.label;
          setTabWithErrors('product-description');
          message.error(`There is an error in the ${tabLabel} tab`, 5);
        }
      }

      if (!hasErrors) {
        setTabWithErrors(null);
      }

      return hasErrors;
    },
    [errors, methods.setError, tabsConfig],
  );

  const handleSubmit = async ({ product, subcategoryName }) => {
    // If there are errors, we need to find the tab with the error
    const hasErrors = validateForm(product);
    if (hasErrors) {
      return;
    }

    showLoading();

    const { product: productFields = [] } = dirtyFields;

    const fieldValues = getValues();

    // If the image is not changed, we should remove it from the request
    if (_.isNull(_.get(productFields, 'image', null))) {
      delete fieldValues.product.image;
    }

    return productUpdate({
      productId: fieldValues.product.id,
      massCreate,
      massUpdate,
      ...fieldValues.product,
      category: getProductCategory({
        category: fieldValues.product.category,
      }),
      // If we have a subcategoryName, we should use it, otherwise we should use the subcategory from the product
      subcategory: subcategoryName ? fieldValues.subcategoryName : fieldValues.product.subcategory,
      instructionsOffPlatform:
        fieldValues.product.isPurchasableOffPlatform &&
        _.isNil(fieldValues.product.instructionsOffPlatform)
          ? DEFAULT_PRODUCT_AIRBNB_INSTRUCTIONS
          : fieldValues.product.instructionsOffPlatform,
    })
      .then((resProduct) => {
        hideLoading();
        mutateProductList();
        if (massCreate) {
          if (!_.isNull(resProduct.product_group_id)) {
            message.success('Product successfully updated and tile added to all stores!');
          } else {
            message.warning(
              'Product successfully updated, but tile cannot be added to all stores because it is already in all stores.',
            );
          }
        } else if (massUpdate) {
          message.success('Product successfully updated in all stores!');
        } else {
          message.success('Product successfully updated!');
        }

        const updatedValues: Partial<Product> = {
          ...resProduct,
          name: resProduct.product_name,
          affiliateLink: resProduct.affiliate_link,
          ownerLocalProductAmount: resProduct.owner_local_product_amount,
          ownerLocalProductCommission: resProduct.owner_local_product_commission,
          externalLink: resProduct.external_link,
          subcategory:
            resProduct.subcategory === ProductSubcategory.SomethingElse
              ? subcategoryName
              : resProduct.subcategory,
          priceType: resProduct.price_type,
          isPurchasableOffPlatform: resProduct.is_purchasable_off_platform,
          instructionsOffPlatform: resProduct.instructions_off_platform,
          amountSplits: resProduct.wholesale_costs,
          percentageSplits: resProduct.percentage_splits,
        };
        onSuccess(updatedValues);
        setButtonText(EDropdownButtonOptions.UPDATE);
        setActiveTab('product-description');
        setTabWithErrors(null);
      })
      .catch((error) => {
        hideLoading();
        setTabWithErrors(null);
        const errorMessage = _.has(error, 'message') ? error.message : null;

        // if the error message is null, check if there are other keys
        if (_.isNull(errorMessage) && !_.isEmpty(error)) {
          Object.keys(error).forEach((key) => {
            const errorVal = error[key];
            // Nested errors inside array of objects (e.g., wholesale_costs, profit_splits)
            if (Array.isArray(errorVal) && errorVal.length > 0 && typeof errorVal[0] === 'object') {
              errorVal.forEach((itemObj, index) => {
                if (itemObj && typeof itemObj === 'object') {
                  Object.keys(itemObj).forEach((fieldKey) => {
                    const fieldError = Array.isArray(itemObj[fieldKey])
                      ? itemObj[fieldKey][0]
                      : itemObj[fieldKey];

                    message.error(
                      `${_.startCase(key)} #${index + 1} (${_.lowerCase(fieldKey)}): ${fieldError}`,
                    );
                  });
                }
              });
              // Standard array of error messages for a single field
            } else if (Array.isArray(errorVal) && errorVal.length > 0) {
              message.error(`${_.startCase(key)}: ${errorVal[0]}`);
              // Single string error message
            } else if (typeof errorVal === 'string') {
              message.error(`${_.startCase(key)}: ${errorVal}`);
            }
          });
        } else {
          // Fallback for unexpected or general error messages
          message.error(errorMessage || 'Something went wrong! Please try again later.');
        }
      });
  };

  const handleFormSubmittion = (event) => {
    event.preventDefault();
    if (massCreate || massUpdate) {
      setConfirmModalOpen(true);
    } else {
      setTabWithErrors(null);
      // In some cases we don't call handleSubmit and for this reason we need to call validateForm here
      methods.handleSubmit(handleSubmit)();
    }
  };

  const handleMenuClick = useCallback((e: any) => {
    setButtonText(e.key as EDropdownButtonOptions);
  }, []);

  const menu = useMemo(
    () => (
      <Menu onClick={handleMenuClick}>
        {buttonText !== EDropdownButtonOptions.UPDATE && (
          <Menu.Item key={EDropdownButtonOptions.UPDATE}>{EDropdownButtonOptions.UPDATE}</Menu.Item>
        )}
        {buttonText === EDropdownButtonOptions.UPDATE && isFromGroup === true && (
          <Menu.Item key={EDropdownButtonOptions.MASS_UPDATE}>
            {EDropdownButtonOptions.MASS_UPDATE}
          </Menu.Item>
        )}
        {buttonText === EDropdownButtonOptions.UPDATE && isFromGroup === false && (
          <Menu.Item key={EDropdownButtonOptions.UPDATE_AND_MASS_CREATE}>
            {EDropdownButtonOptions.UPDATE_AND_MASS_CREATE}
          </Menu.Item>
        )}
      </Menu>
    ),
    [buttonText, isFromGroup],
  );

  const handleConfirmMassCreate = () => {
    methods.handleSubmit(handleSubmit)();
    setIsMassCreate(false);
    setConfirmModalOpen(false);
  };

  const handleCancelMassCreate = useCallback(() => {
    setConfirmModalOpen(false);
  }, []);

  const handleConfirmMassUpdate = () => {
    methods.handleSubmit(handleSubmit)();
    setIsMassUpdate(false);
    setConfirmModalOpen(false);
  };

  const handleCancelMassUpdate = useCallback(() => {
    setConfirmModalOpen(false);
  }, []);

  const handleCancel = useCallback(() => {
    onCancel();
    setButtonText(EDropdownButtonOptions.UPDATE);
    setActiveTab('product-description');
    setTabWithErrors(null);
  }, [onCancel]);

  return (
    <Modal
      className="EditProductModal"
      visible={isOpen}
      footer={null}
      destroyOnClose
      maskClosable={false}
      onCancel={handleCancel}
    >
      <FormProvider
        formState={{ dirtyFields, isSubmitting, errors, ...restFormState }}
        getValues={getValues}
        {...methods}
      >
        <form className="EditProductModal-form" onSubmit={handleFormSubmittion}>
          <Tabs centered activeKey={activeTab} onChange={handleChangeTab}>
            {Object.entries(tabsConfig).map(([key, { label, title, children }]) => (
              <Tabs.TabPane
                tab={
                  <span style={{ color: tabWithErrors === key ? 'red' : 'inherit' }}>
                    {label} {tabWithErrors === key && <WarningOutlined style={{ color: 'red' }} />}
                  </span>
                }
                key={key}
              >
                <div className="EditProductModal-tab-container">
                  <Typography variant="h6" className="EditProductModal-tab-container__title">
                    {title}
                  </Typography>
                  <div className="EditProductModal-tab-container__content">{children}</div>
                </div>
              </Tabs.TabPane>
            ))}
          </Tabs>
          <div className="EditProductModal-footer">
            <>
              <Button variant="text" color="primary" onClick={handleCancel}>
                Cancel
              </Button>
              <Dropdown.Button
                className="dropdown-btn"
                overlay={menu}
                placement="bottomRight"
                trigger={['click']}
                htmlType="submit"
                disabled={isSubmitting}
              >
                {buttonText}
              </Dropdown.Button>
              {isFromGroup === true ? (
                <ConfirmModalMassUpdateProduct
                  isOpen={confirmModalOpen}
                  onConfirm={handleConfirmMassUpdate}
                  onCancel={handleCancelMassUpdate}
                />
              ) : (
                <ConfirmModalMassAddProduct
                  isOpen={confirmModalOpen}
                  onConfirm={handleConfirmMassCreate}
                  onCancel={handleCancelMassCreate}
                />
              )}
            </>
          </div>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default EditProductModal;
